import DepositWithGlideModal from "./DepositWithGlideModal";
import { GlideWalletProvider } from "./components/wallet/GlideWalletProvider";
import { restoreCoinbaseConnector, wagmiConfig } from "./lib/wagmi";
import { WagmiProvider } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GlideDepositConfigContext } from "./lib/useDepositConfig";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CAIP2,
  caip2FromCAIP19,
  Chain,
  createGlideConfig,
  Currency,
  CurrencyNotSupportedError,
  getSessionById,
  listSupportedCurrencies,
  Session,
} from "@paywithglide/glide-js";
import { solana } from "@paywithglide/glide-js/chains";

import { fromHex, Hex, isHex } from "viem";
import { useAppTheme } from "./lib/useAppTheme";
import { supportedChains } from "./lib/glide";
import ErrorModal from "./ErrorModal";
import { AppConfig, appConfigs } from "./lib/appConfig";
import { Theme, createDefaultTheme } from "./lib/theme";
import { isCAIP19AddressToken } from "./lib/isCAIP19AddressToken";
import { contractAddressFromCAIP19 } from "./lib/contractAddressFromCAIP19";
import { FundingSource } from "./lib/fundingSource";
import { logger } from "./lib/logger";
import memStore from "./lib/memStore";
import { glideLocalStorage } from "./lib/glideLocalStorage";
import { removeEmpty } from "./lib/removeEmpty";
import * as Sentry from "@sentry/react";
import { initMobileWalletReconnect } from "./lib/mobileWalletReconnect";
import { widgetAnalytics } from "./lib/analytics";
import { Dialog } from "./catalyst/dialog";
import { AnimateChangeInHeight } from "./components/AnimateChangeInHeight";
import PrimaryButton from "./components/PrimaryButton";
import Alert from "./components/Alert";
import { CircleCheck } from "lucide-react";

export type Props = {
  children?: (renderProps: {
    openTransactionModal: () => void;
  }) => React.ReactNode;
};

const queryClient = new QueryClient();

export default function DepositWithGlide({ children }: Props) {
  // Initialize mobile wallet reconnection
  useEffect(() => {
    initMobileWalletReconnect();
  }, []);
  // Coinbase's connector is stripped from wagmiConfig() before mount so
  // WagmiProvider's default reconnectOnMount never probes its buggy
  // provider. Add it back now (post-mount) so it's listed for manual
  // connect — this never re-triggers an automatic reconnect pass.
  useEffect(() => restoreCoinbaseConnector(), []);
  const [, appSlug] = window.location.pathname.split("/");
  const params = new URLSearchParams(window.location.search);
  const embed = params.get("embed") === "true";
  const [autoCloseOnSuccess, setAutoCloseOnSuccess] = useState<boolean>(
    embed && params.get("autoCloseOnSuccess") === "true",
  );
  const [preferGaslessPaymentOverride, setPreferGaslessPaymentOverride] =
    useState<boolean>(params.get("preferGaslessPayment") === "true");
  const [evmTx, setEvmTx] = useState<
    { chainId: number; to: Hex; data: Hex; value: bigint } | undefined
  >(
    params.has("evm.chainId") && params.has("evm.to")
      ? {
          chainId: Number(params.get("evm.chainId")),
          to: params.get("evm.to") as Hex,
          data: params.get("evm.data") as Hex,
          value: fromHex(params.get("evm.value") as Hex, { to: "bigint" }),
        }
      : undefined,
  );
  const [tokenApproval, setTokenApproval] = useState<
    { token: Hex; amount: bigint } | undefined
  >(
    params.has("approval.token") && params.has("approval.amount")
      ? {
          token: params.get("approval.token") as Hex,
          amount: BigInt(params.get("approval.amount") || 0),
        }
      : undefined,
  );
  const [sessionId, setSessionId] = useState<string | undefined>(
    params.get("sessionId") || undefined,
  );
  const [glideSessionId, setGlideSessionId] = useState<string | undefined>(
    params.get("glideSessionId") || undefined,
  );
  const [refundToken] = useState<string | undefined>(
    params.get("refundToken") || undefined,
  );
  const [sessionMetadata, setSessionMetadata] = useState<string | undefined>(
    params.get("metadata") || undefined,
  );

  const [isModalOpen, setIsModalOpen] = React.useState(!embed);
  const [recipient, setRecipient] = useState<string | undefined>(
    params.get("recipient") || undefined,
  );
  const [mode, setMode] = useState<string | undefined>(
    params.get("mode") || undefined,
  );
  const [amount, setAmount] = useState<string | undefined>(
    params.get("amount") || "",
  );
  const [amountUSD, setAmountUSD] = useState<string | undefined>(
    params.get("amountUSD") || "",
  );
  const [commissionRates, setCommissionRates] =
    useState<Record<"tier1" | "tier2" | "tier3", string>>();
  const [chainIds, setChainIds] = useState<CAIP2[]>(
    (params.get("chainIds")?.split(",") as CAIP2[]) || [],
  );
  const [excludeChainIds, setExcludeChainIds] = useState<CAIP2[]>(
    (params.get("excludeChainIds")?.split(",") as CAIP2[]) || [],
  );
  const [excludeCurrencyTiers, setExcludeCurrencyTiers] = useState<
    ("tier1" | "tier2" | "tier3")[]
  >(
    (params.get("excludeCurrencyTiers")?.split(",") as (
      | "tier1"
      | "tier2"
      | "tier3"
    )[]) || [],
  );
  const [canSwitchMode, setCanSwitchMode] = useState<boolean>(
    !params.has("mode"),
  );
  const [fundingSources, setFundingSources] = useState<FundingSource[]>(
    (params.get("fundingSources")?.split(",") as FundingSource[]) || [],
  );
  const [excludeFundingSources, setExcludeFundingSources] = useState<
    FundingSource[]
  >((params.get("excludeFundingSources")?.split(",") as FundingSource[]) || []);
  const [allowedPaymentCurrencies, setAllowedPaymentCurrencies] = useState<
    string[]
  >(params.get("allowedPaymentCurrencies")?.split(",").filter(Boolean) || []);
  const [excludedPaymentCurrencies, setExcludedPaymentCurrencies] = useState<
    string[]
  >(params.get("excludePaymentCurrencies")?.split(",").filter(Boolean) || []);
  const [defaultPaymentCurrency, setDefaultPaymentCurrency] = useState<
    string | undefined
  >(params.get("defaultPaymentCurrency") || undefined);
  const [chooseCurrencyFirst, setChooseCurrencyFirst] = useState<boolean>(
    params.get("chooseCurrencyFirst") === "true",
  );
  const [disableWithdrawToSelfSuggestion, setDisableWithdrawToSelfSuggestion] =
    useState<boolean>(params.get("disableWithdrawToSelfSuggestion") === "true");
  const [hideSettlementCopy, setHideSettlementCopy] = useState<boolean>(
    params.get("hideSettlementCopy") === "true",
  );
  const [showAppLogo, setShowAppLogo] = useState<
    "default" | "always" | "never"
  >(
    params.has("showAppLogo")
      ? (params.get("showAppLogo") as "default" | "always" | "never")
      : "default",
  );
  const [showOnrampFirst, setShowOnrampFirst] = useState<boolean | undefined>(
    params.has("showOnrampFirst")
      ? params.get("showOnrampFirst") === "true"
      : undefined,
  );
  const [debitCardAlias, setDebitCardAlias] = useState<
    "coinbase" | "fiat" | undefined
  >(
    params.has("debitCardAlias")
      ? (params.get("debitCardAlias") as "coinbase" | "fiat")
      : undefined,
  );
  const [copyOverrides, setCopyOverrides] = useState<
    Record<string, string> | undefined
  >(
    params.has("copyOverrides")
      ? JSON.parse(params.get("copyOverrides")!)
      : undefined,
  );

  const [appMetadataName, setAppMetadataName] = useState<string | undefined>(
    params.get("appMetadata.name") || undefined,
  );
  const [appMetadataLogoUrl, setAppMetadataLogoUrl] = useState<
    string | undefined
  >(params.get("appMetadata.logoUrl") || undefined);
  const [appMetadataFaviconUrl, setAppMetadataFaviconUrl] = useState<
    string | undefined
  >(params.get("appMetadata.faviconUrl") || undefined);

  const [enableRefundEmails, setEnableRefundEmails] = useState<
    boolean | undefined
  >(params.get("enableRefundEmails") === "true" ? true : undefined);
  const [payerEmail, setPayerEmail] = useState<string | undefined>(
    params.get("payerEmail") || undefined,
  );

  const [connectedWalletAddress, setConnectedWalletAddress] = useState<
    string | undefined
  >(params.get("connectedWalletAddress") || undefined);

  const [stableDepositAddressKey, setStableDepositAddressKey] = useState<
    string | undefined
  >(params.get("stableDepositAddressKey") || undefined);

  // userPhoneNumber is ONLY set from widget session API, not from URL params (security)
  const [userPhoneNumber, setUserPhoneNumber] = useState<string | undefined>();

  const [settleCurrencyCAIP19, setSettleCurrencyCAIP19] = useState<
    string | undefined
  >();
  const [settleCurrency, setSettleCurrency] = useState<Currency | undefined>();
  const [settleChain, setSettleChain] = useState<Chain | undefined>();

  const [safeInsetBottom, setSafeInsetBottom] = useState<number>(0);

  const [isInitialized, setIsInitialized] = useState(false);
  const [didLoadSession, setDidLoadSession] = useState(false);
  const [refundSession, setRefundSession] = useState<Session | undefined>();
  const [didLoadRefundSession, setDidLoadRefundSession] = useState(false);

  const waitForInit = params.get("init") === "true";
  const [debug, setDebug] = useState(params.get("debug") === "true");
  const [popupsBlocked, setPopupsBlocked] = useState(
    params.get("popupsBlocked") === "true",
  );
  const [gasRefuelAmountPerChain, setGasRefuelAmountPerChain] = useState<Record<
    string,
    string
  > | null>(
    params.get("gasRefuelAmountPerChain")
      ? JSON.parse(params.get("gasRefuelAmountPerChain")!)
      : {},
  );

  const [didProvideLocalStorage, setDidProvideLocalStorage] = useState(false);

  const [chain, setChain] = useState<Chain | undefined>();
  const [currency, setCurrency] = useState<Currency | undefined>();
  const [appConfig, setAppConfig] = useState<AppConfig | undefined>();
  const [themeOverrides, setThemeOverrides] = useState<Theme>();
  const [chainIdOverride, setChainIdOverride] = useState<CAIP2 | undefined>(
    (params.get("chainId") as CAIP2) || undefined,
  );
  const [currencyIdOverride, setCurrencyIdOverride] = useState<
    string | undefined
  >((params.get("currencyId") as string) || undefined);
  const [loadError, setLoadError] = useState(false);
  const [sdkVersion, setSdkVersion] = useState<string | undefined>();
  const [referrerDomain, setReferrerDomain] = useState<string | undefined>();
  const [didResetLocalStorage, setDidResetLocalStorage] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  useEffect(() => {
    if (mode) {
      glideLocalStorage.setItem("glide.mode", mode);
    } else {
      glideLocalStorage.removeItem("glide.mode");
    }
  }, [mode]);

  // Set SDK version and referrer domain in Sentry context
  useEffect(() => {
    if (sdkVersion || referrerDomain) {
      Sentry.setContext("sdk_info", {
        version: sdkVersion,
        referrer_domain: referrerDomain,
      });

      if (sdkVersion) {
        Sentry.setTag("sdk_version", sdkVersion);
      }
      if (referrerDomain) {
        Sentry.setTag("referrer_domain", referrerDomain);
      }

      // Send a Sentry message so we can see this even without errors
      Sentry.captureMessage(
        `Widget opened - SDK v${sdkVersion} from ${referrerDomain}`,
        {
          level: "info",
          tags: {
            sdk_version: sdkVersion,
            referrer_domain: referrerDomain,
          },
        },
      );

      logger.info(
        `SDK context set - version: ${sdkVersion}, referrer: ${referrerDomain}`,
        { publish: true },
      );
    }
  }, [sdkVersion, referrerDomain]);

  useEffect(() => {
    if (embed) {
      const timer = setTimeout(() => {
        setIsModalOpen(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [embed]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      try {
        if (event.data.type === "updateTheme") {
          setThemeOverrides(event.data.theme);
        }

        if (event.data.type === "initialize") {
          logger.info(
            `initialize event received: ${JSON.stringify(event.data)}`,
            { publish: true },
          );

          // Update analytics with SDK-provided data and enable client callback
          widgetAnalytics.initializeFromSDK({
            hasAnalyticsCallback: event.data.hasAnalyticsCallback || false,
            projectId: event.data.projectId,
            appSlug: event.data.appSlug,
            sdkVersion: event.data.sdkVersion,
            referrerDomain: event.data.referrerDomain,
            connectedWalletAddress: event.data.connectedWalletAddress,
          });

          setSessionId(event.data.sessionId);
          setGlideSessionId(event.data.glideSessionId);
          setAmount(event.data.amount);
          setAmountUSD(event.data.amountUSD);
          setTokenApproval(event.data.tokenApproval);
          setAutoCloseOnSuccess(event.data.autoCloseOnSuccess);
          setEvmTx(event.data.evmTx);
          setMode(event.data.mode);
          setPreferGaslessPaymentOverride(event.data.preferGaslessPayment);
          setRecipient(event.data.recipient);
          setSessionMetadata(event.data.sessionMetadata);
          setThemeOverrides(event.data.theme);
          setCanSwitchMode(!event.data.mode);
          setCommissionRates(event.data.commissionRates);
          setChainIds(event.data.chainIds);
          setExcludeChainIds(event.data.excludeChainIds);
          setExcludeCurrencyTiers(event.data.excludeCurrencyTiers);
          setFundingSources(event.data.fundingSources || []);
          setExcludeFundingSources(event.data.excludeFundingSources || []);
          setAllowedPaymentCurrencies(
            event.data.allowedPaymentCurrencies || [],
          );
          setExcludedPaymentCurrencies(
            event.data.excludePaymentCurrencies || [],
          );
          setDefaultPaymentCurrency(
            event.data.defaultPaymentCurrency || undefined,
          );
          setDidProvideLocalStorage(event.data.didProvideLocalStorage || false);
          setDebug(event.data.debug || false);
          setPopupsBlocked(event.data.popupsBlocked || false);
          setGasRefuelAmountPerChain(event.data.gasRefuelAmountPerChain);
          setChainIdOverride(event.data.chainId);
          setCurrencyIdOverride(event.data.currencyId);
          setChooseCurrencyFirst(event.data.chooseCurrencyFirst || false);
          setDisableWithdrawToSelfSuggestion(
            event.data.disableWithdrawToSelfSuggestion || false,
          );
          setHideSettlementCopy(event.data.hideSettlementCopy || false);
          setShowAppLogo(event.data.showAppLogo || "default");
          setShowOnrampFirst(event.data.showOnrampFirst);
          setDebitCardAlias(event.data.debitCardAlias);
          setCopyOverrides(event.data.copyOverrides);

          if (event.data.appMetadata) {
            setAppMetadataName(event.data.appMetadata.name);
            setAppMetadataLogoUrl(event.data.appMetadata.logoUrl);
            setAppMetadataFaviconUrl(event.data.appMetadata.faviconUrl);
          }

          setEnableRefundEmails(event.data.enableRefundEmails);
          setPayerEmail(event.data.payerEmail);
          setStableDepositAddressKey(
            event.data.stableDepositAddressKey || undefined,
          );
          setSafeInsetBottom(event.data.theme?.safeInsetBottom || 0);
          setSdkVersion(event.data.sdkVersion);
          setReferrerDomain(event.data.referrerDomain);

          memStore.clear();

          logger.info(
            `Setting didProvideLocalStorage=${event.data.didProvideLocalStorage}`,
          );
          memStore.setItem(
            "didProvideLocalStorage",
            Boolean(event.data.didProvideLocalStorage),
          );

          if (event.data.connectedWalletAddress) {
            memStore.setItem(
              "connectedWalletAddress",
              event.data.connectedWalletAddress,
            );
            setConnectedWalletAddress(event.data.connectedWalletAddress);
          }

          if (event.data.currentChainId) {
            memStore.setItem("currentChainId", event.data.currentChainId);
          }

          if (event.data.availableChainIds) {
            memStore.setItem("availableChainIds", event.data.availableChainIds);
          }

          setDidLoadSession(!event.data.sessionId);
          setIsInitialized(true);
        }
      } catch {
        setLoadError(true);
      }
    }

    window.addEventListener("message", handleMessage);

    window.parent?.postMessage({ type: "sendInit" }, "*");

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        let appConfig = appSlug in appConfigs ? appConfigs[appSlug] : undefined;

        if (!appConfig) {
          const resp = await fetch(
            (import.meta.env.VITE_GLIDE_BASE_URL ||
              "https://api.paywithglide.xyz") +
              "/widget-config?slug=" +
              appSlug,
          );
          if (resp.ok) {
            const appConfigData = await resp.json();

            appConfig = {
              ...appConfigData,
              chain: supportedChains.find(
                (c) => c.id === appConfigData.chainId,
              ),
              currency: {
                id: appConfigData.currency.id,
                name: appConfigData.currency.name,
                symbol: appConfigData.currency.symbol,
                decimals: appConfigData.currency.decimals,
                logoUrl: appConfigData.currency.logoUrl,
                on: (chain: Chain) => {
                  if (
                    chain.id === caip2FromCAIP19(appConfigData.currency.caip19)
                  ) {
                    return appConfigData.currency.caip19;
                  }
                  throw new CurrencyNotSupportedError();
                },
                contractAddressOn: isCAIP19AddressToken(
                  appConfigData.currency.caip19,
                )
                  ? (chain: Chain) => {
                      if (
                        chain.id ===
                        caip2FromCAIP19(appConfigData.currency.caip19)
                      ) {
                        return contractAddressFromCAIP19(
                          appConfigData.currency.caip19,
                        );
                      }
                      throw new CurrencyNotSupportedError();
                    }
                  : undefined,
              },
            };
          }
        }

        if (appConfig && chainIdOverride) {
          appConfig.chain = supportedChains.find(
            (c) => c.id === chainIdOverride,
          );

          logger.info(`App config chain overridden to ${appConfig?.chain?.id}`);
        }

        if (appConfig && currencyIdOverride) {
          const currencies = await listSupportedCurrencies(
            createGlideConfig({
              baseUrl: import.meta.env.VITE_GLIDE_BASE_URL || undefined,
              projectId:
                import.meta.env.VITE_GLIDE_PROJECT_ID ||
                "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // glide-apps
              chains: [appConfig.chain!],
            }),
            {
              chainId: appConfig.chain!.id,
              all: true,
            },
          );

          appConfig.currency = currencies.find(
            (c) => c.id === currencyIdOverride,
          );

          logger.info(
            `App config currency overridden to ${appConfig?.currency?.id}`,
          );
        }

        setAppConfig(appConfig);

        // Initialize internal analytics early (for our Mixpanel tracking)
        // This works regardless of SDK version - uses URL slug and appConfig projectId
        if (appConfig?.projectId) {
          widgetAnalytics.initializeInternal({
            projectId: appConfig.projectId,
            appSlug: appSlug,
          });
        }

        return;
      } catch (e) {
        logger.error(`Failed to load app config: ${e}`);
        setLoadError(true);
      }
    };

    if (!waitForInit || isInitialized) {
      loadConfig();
    }
  }, [
    appSlug,
    chainIdOverride,
    currencyIdOverride,
    isInitialized,
    waitForInit,
  ]);

  useEffect(() => {
    const loadSession = async () => {
      const sessionResp = await fetch(`
                ${
                  import.meta.env.VITE_GLIDE_BASE_URL ||
                  "https://api.paywithglide.xyz"
                }/widget/get-session?session_id=${sessionId}`);

      if (!sessionResp.ok) {
        logger.error("Failed to fetch session");
        setLoadError(true);
        setDidLoadSession(true);
        return;
      }

      const session = await sessionResp.json();

      setMode(session.mode);
      setSessionMetadata(session.metadata);
      setCommissionRates(session.commissionRates);
      setChainIds(session.chainIds);
      setFundingSources(session.fundingSources);
      setExcludeChainIds(session.excludeChainIds);
      setExcludeCurrencyTiers(session.excludeCurrencyTiers);
      setExcludeFundingSources(session.excludeFundingSources);
      // Only override if session has these values - don't clear existing values
      if (session.allowedPaymentCurrencies) {
        setAllowedPaymentCurrencies(session.allowedPaymentCurrencies);
      }
      // Handle both excludedPaymentCurrencies and excludePaymentCurrencies for compatibility
      if (
        session.excludedPaymentCurrencies ||
        session.excludePaymentCurrencies
      ) {
        setExcludedPaymentCurrencies(
          session.excludedPaymentCurrencies || session.excludePaymentCurrencies,
        );
      }

      // Set userPhoneNumber from widget session (only source - not from URL params)
      if (session.userPhoneNumber) {
        setUserPhoneNumber(session.userPhoneNumber);
      }

      // Set settleCurrency from widget session if provided
      if (session.settleCurrency) {
        setSettleCurrencyCAIP19(session.settleCurrency);
      }

      switch (session.mode) {
        case "pay": {
          setAmount(session.payAmount);
          break;
        }
        case "deposit": {
          setRecipient(session.recipient);
          break;
        }
        case "withdraw": {
          break;
        }
        default:
          throw new Error(`Unsupported mode: ${session.mode}`);
      }

      setDidLoadSession(true);
    };

    if (sessionId) {
      loadSession();
    }
  }, [sessionId]);

  useEffect(() => {
    const loadRefundSession = async () => {
      const glideConfig = createGlideConfig({
        baseUrl: import.meta.env.VITE_GLIDE_BASE_URL || undefined,
        projectId:
          import.meta.env.VITE_GLIDE_PROJECT_ID ||
          "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // glide-apps
        chains: supportedChains,
      });

      try {
        const session = await getSessionById(glideConfig, glideSessionId!);
        setRefundSession(session);
        setDidLoadRefundSession(true);
      } catch (error) {
        logger.error(`Failed to load refund session: ${error}`);
        setLoadError(true);
        setDidLoadRefundSession(true);
      }
    };

    if (mode === "refund" && glideSessionId) {
      loadRefundSession();
    } else if (mode === "refund") {
      // If mode is refund but no glideSessionId, mark as loaded to show error
      setDidLoadRefundSession(true);
    }
  }, [mode, glideSessionId]);

  const effectiveChain =
    settleChain || (mode === "buy" ? chain : appConfig?.chain);
  const preferGaslessPayment =
    effectiveChain?.id === solana.id ? true : preferGaslessPaymentOverride;

  useEffect(() => {
    const loadSettleCurrency = async () => {
      if (!settleCurrencyCAIP19 || !appConfig) return;

      try {
        const chainId = caip2FromCAIP19(settleCurrencyCAIP19);
        const chain = supportedChains.find((c) => c.id === chainId);

        if (!chain) {
          logger.error(
            `Chain not found for settleCurrency CAIP19: ${settleCurrencyCAIP19}`,
          );
          return;
        }

        const currencies = await listSupportedCurrencies(
          createGlideConfig({
            baseUrl: import.meta.env.VITE_GLIDE_BASE_URL || undefined,
            projectId:
              import.meta.env.VITE_GLIDE_PROJECT_ID || appConfig.projectId,
            chains: [chain],
          }),
          {
            chainId: chain.id,
            all: true,
          },
        );

        const currency = currencies.find((c) => {
          try {
            return c.on(chain) === settleCurrencyCAIP19;
          } catch {
            return false;
          }
        });

        if (currency) {
          setSettleCurrency(currency);
          setSettleChain(chain);
          logger.info(
            `Settle currency loaded: ${currency.id} on chain ${chain.id} from CAIP19: ${settleCurrencyCAIP19}`,
          );
        } else {
          logger.error(
            `Currency not found for settleCurrency CAIP19: ${settleCurrencyCAIP19}`,
          );
        }
      } catch (error) {
        logger.error(`Failed to load settle currency: ${error}`);
      }
    };

    loadSettleCurrency();
  }, [settleCurrencyCAIP19, appConfig]);

  const onClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const resetLocalStorage = useCallback(() => {
    try {
      window.localStorage.clear();
      setDidResetLocalStorage(true);
      setResetError(null);
      logger.info("Reset route cleared localStorage");
    } catch (error) {
      logger.error(`Failed to clear localStorage: ${error}`);
      setResetError("Failed to clear local storage.");
      setDidResetLocalStorage(false);
    }
  }, []);

  const appConfigWithOverrides: AppConfig | undefined = useMemo(() => {
    if (!appConfig) return;

    let paymentMethods: AppConfig["paymentMethods"];
    if (fundingSources?.length) {
      paymentMethods = fundingSources as AppConfig["paymentMethods"];
    } else if (mode === "withdraw") {
      // For withdrawals, default to wallet only
      paymentMethods = ["wallet"];
    } else {
      // For other modes (deposit, pay, etc), use app config
      paymentMethods = appConfig.paymentMethods;
    }

    return {
      ...appConfig,
      name: appMetadataName || appConfig.name,
      logoUrl: appMetadataLogoUrl || appConfig.logoUrl,
      faviconUrl: appMetadataFaviconUrl || appConfig.faviconUrl,
      paymentMethods,
      theme: {
        ...createDefaultTheme(),
        ...removeEmpty(appConfig.theme || {}),
        ...removeEmpty(themeOverrides || {}),
      },
    } as AppConfig;
  }, [
    appConfig,
    appMetadataName,
    appMetadataLogoUrl,
    appMetadataFaviconUrl,
    fundingSources,
    mode,
    themeOverrides,
  ]);

  const widgetConfig = useMemo(
    () => ({
      appMetadata: {
        id: appSlug,
        name: appConfigWithOverrides?.name,
        logoUrl: appConfigWithOverrides?.logoUrl,
        faviconUrl: appConfigWithOverrides?.faviconUrl,
      },
      theme: appConfigWithOverrides?.theme,
    }),
    [
      appSlug,
      appConfigWithOverrides?.name,
      appConfigWithOverrides?.logoUrl,
      appConfigWithOverrides?.faviconUrl,
      appConfigWithOverrides?.theme,
    ],
  );

  useAppTheme(appConfigWithOverrides);

  const initializing =
    (!loadError && !appConfigWithOverrides) ||
    (waitForInit && !isInitialized) ||
    (sessionId && !didLoadSession) ||
    (mode === "refund" && !didLoadRefundSession);

  useEffect(() => {
    if (!initializing) {
      window.parent?.postMessage({ type: "didOpen" }, "*");
    }
  }, [initializing]);

  if (initializing) {
    return null;
  }

  if (!appConfigWithOverrides) {
    return (
      <ErrorModal
        open={isModalOpen}
        onClose={embed ? () => setIsModalOpen(!embed) : undefined}
        title="Unknown app"
        subtitle="Verify the app name in the URL or contact Glide team for support"
      />
    );
  }

  if (loadError) {
    return (
      <ErrorModal
        open={isModalOpen}
        onClose={embed ? () => setIsModalOpen(!embed) : undefined}
        title="Error"
        subtitle="Sorry, there was an error. Please try again later."
      />
    );
  }

  // Allow pay mode without amount for arbitrary amounts
  // if (mode === "pay" && !Number(amount)) {
  //   return (
  //     <ErrorModal
  //       open={isModalOpen}
  //       onClose={embed ? () => setIsModalOpen(!embed) : undefined}
  //       title="Invalid params"
  //       subtitle="The amount param is missing or invalid"
  //     />
  //   );
  // }

  if (mode === "refund" && !glideSessionId) {
    return (
      <ErrorModal
        open={isModalOpen}
        onClose={embed ? () => setIsModalOpen(!embed) : undefined}
        title="Invalid params"
        subtitle="Refund mode requires a glideSessionId parameter"
      />
    );
  }

  const payModeRecipient = recipient || appConfigWithOverrides.payRecipient;
  if (mode === "pay" && !appConfigWithOverrides.payRecipient) {
    return (
      <ErrorModal
        open={isModalOpen}
        onClose={embed ? () => setIsModalOpen(!embed) : undefined}
        title="Invalid params"
        subtitle="Pay mode is not supported for this app. Contact Glide team for support"
      />
    );
  }

  if (
    mode === "call" &&
    (!evmTx || !evmTx.chainId || !isHex(evmTx.to) || !isHex(evmTx.data))
  ) {
    return (
      <ErrorModal
        open={isModalOpen}
        onClose={embed ? () => setIsModalOpen(!embed) : undefined}
        title="Invalid params"
        subtitle="The evm param is missing or invalid"
      />
    );
  }

  if (mode === "reset") {
    return (
      <div className={embed ? "" : "min-h-dvh bg-[--color-modal-backdrop]"}>
        <Dialog open={isModalOpen} onClose={onClose} size="sm">
          <AnimateChangeInHeight>
            <div className="px-4 pb-5 pt-6">
              {appConfigWithOverrides.logoUrl ? (
                <div className="mb-4 flex flex-col items-center justify-center">
                  <img
                    src={appConfigWithOverrides.logoUrl}
                    className="h-[--app-logo-height] w-auto"
                    alt={`${appConfigWithOverrides.name} Logo`}
                  />
                </div>
              ) : null}

              <div className="text-center text-[--color-text-primary] text-sm font-medium">
                Reset data
              </div>
              <div className="mt-1 text-center text-[--color-text-secondary] text-xs">
                Your wallet connections and preferences will be reset.
              </div>
              {!didResetLocalStorage ? (
                <div className="mt-5">
                  <PrimaryButton onClick={resetLocalStorage}>
                    Reset data
                  </PrimaryButton>
                </div>
              ) : null}
              {didResetLocalStorage ? (
                <Alert
                  className="mt-3"
                  icon={<CircleCheck className="h-5 w-5" />}
                  title="Reset complete"
                  subtitle={
                    <>
                      You can head back to the app and deposit again. If you run
                      into any issues,{" "}
                      <a
                        href="https://docs.glide.link/support"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2"
                      >
                        contact support
                      </a>
                      .
                    </>
                  }
                />
              ) : null}
              {resetError ? (
                <div className="mt-3 text-center text-red-500 text-sm">
                  {resetError}
                </div>
              ) : null}
            </div>
          </AnimateChangeInHeight>
        </Dialog>
      </div>
    );
  }

  return (
    <>
      {children?.({
        openTransactionModal: () => {
          setIsModalOpen(true);
        },
      })}

      <GlideDepositConfigContext.Provider
        value={{
          appName: appConfigWithOverrides.name,
          appLogoUrl: appConfigWithOverrides.logoUrl,
          appFaviconUrl: appConfigWithOverrides.faviconUrl,
          chain:
            settleChain ||
            (mode === "buy" ? chain : appConfigWithOverrides.chain),
          currency:
            settleCurrency ||
            (mode === "buy" ? currency : appConfigWithOverrides.currency),
          mode:
            mode === "deposit" ||
            mode === "withdraw" ||
            mode === "pay" ||
            mode === "call" ||
            mode === "buy" ||
            mode === "refund"
              ? mode
              : undefined,
          recipient: mode === "pay" ? payModeRecipient : recipient || undefined,
          amount: amount || "",
          amountUSD: amountUSD || undefined,
          hasFixedAmount:
            (!!amount && Number(amount) > 0) ||
            (!!amountUSD && Number(amountUSD) > 0),
          evmTx,
          tokenApproval,
          canSwitchMode,

          connectedWalletAddress,

          gasRefuelAmountPerChain,
          preferGaslessPayment,
          chainIds,
          excludeChainIds,
          excludeCurrencyTiers,
          excludeFundingSources,
          allowedPaymentCurrencies,
          excludedPaymentCurrencies,
          defaultPaymentCurrency,

          embed,
          autoCloseOnSuccess,

          chooseCurrencyFirst,

          disableWithdrawToSelfSuggestion,

          glideConfig: createGlideConfig({
            baseUrl: import.meta.env.VITE_GLIDE_BASE_URL || undefined,
            projectId:
              import.meta.env.VITE_GLIDE_PROJECT_ID ||
              appConfigWithOverrides.projectId,
            chains: supportedChains,
          }),

          withdrawChains: appConfigWithOverrides.withdrawChains,

          paymentMethods: appConfigWithOverrides.paymentMethods || [
            "wallet",
            "external_wallet",
            "transfer",
            "coinbase",
            "fiat",
          ],

          defaultDepositTokenDenomination:
            appConfigWithOverrides.defaultDepositTokenDenomination,

          commissionRates,

          sessionMetadata,
          whitelabel: appConfigWithOverrides.whitelabel,
          widgetSessionId: sessionId,
          glideSessionId,
          refundSession,
          refundToken,

          didProvideLocalStorage,

          setMode,
          setRecipient,
          setChain,
          setCurrency,

          debug,
          popupsBlocked,
          showSupportedTokensMessage:
            appConfigWithOverrides.showSupportedTokensMessage,
          hideSettlementCopy:
            hideSettlementCopy || appConfigWithOverrides.hideSettlementCopy,
          tokenAmountDefault: appConfigWithOverrides.tokenAmountDefault,
          showAppLogo,
          showOnrampFirst,
          debitCardAlias,
          copyOverrides,

          enableRefundEmails,
          payerEmail,
          userPhoneNumber,
          stableDepositAddressKey,

          safeInsetBottom,

          widgetConfig,
        }}
      >
        <WagmiProvider config={wagmiConfig()}>
          <QueryClientProvider client={queryClient}>
            <GlideWalletProvider>
              <DepositWithGlideModal open={isModalOpen} onClose={onClose} />
            </GlideWalletProvider>
          </QueryClientProvider>
        </WagmiProvider>
      </GlideDepositConfigContext.Provider>
    </>
  );
}
